import { HttpClient, HttpParams } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { Helicopter } from "../shared/models/helicopter.model";
import { HoldItemList } from "../shared/models/holdItemList.model";
import { HtlBlock } from "../shared/models/htlBlock.model";
import { HTlDocument } from "../shared/models/htlDocument.model";
import { NextScheduledInpsection } from "../shared/models/nextScheduledInpsection.model";
import moment from "moment";
import { v4 as uuid } from "uuid";

enum EndpointRoutes {
    INSERT_HTL_BLOCK = "htlBlock/insert",
    GET_HTL_BLOCKS = "htlBlock/get",
    UPDATE_HTL_BLOCK = "htlBlock/update",
    GET_HTL_DOCUMENTS = "htlDocument/get",
    GET_HTL_BLOCKS_NUMBERS = "htlBlock/getBlockNumbers",
    GET_HOLD_ITEM_LIST = "holdItemList/get",
    INSERT_HOLD_ITEM_LIST = "holdItemList/insert",
    UPDATE_EXPIRING_HOLD_ITEM_LIST = "holdItemList/updateExpiring",
    UPDATE_NEXT_SCHEDULED_INSPECTION = "schedule/save",
    GENERATE_HTL_DOC = "excel/generate",
    GENERATE_HOLD_ITEM_PDF = "excel/generateHoldItemList",
    GENERATE_SHORT_TERMS_PDF = "excel/generateShortFrequencyList",
    GENERATE_HTL_PUBLIC_PDF = "maintenance/excel/generate",
    SAVE_EDITED_COLUMNS = "htlDocument/saveEditedColumns",
    GET_ENABLED_CELLS = "htlDocument/getEnabledColumns",
    GET_EDITED_CELLS = "htlDocument/getLastEditedColumns",
    GET_DAILY_CHECKS = "dailyChecks/get",
    IS_ALLOWED = "htlDocument/isAllowed",
    APPROVE_DOCUMENT = "htlDocument/approve",
}

export class HtlBlockNumber {
    id: number;
    blockNumber: number;
    status: number;
}

export class Expiring {
    id: number;
    exe1Document: string;
    exe1Date: string;
    exe1FlightHours: string;
    exe1FlightNumber: string;
    exe1Cycles: number;
    exe2Document: string;
    exe2Date: string;
    exe2FlightHours: string;
    exe2FlightNumber: string;
    exe2Cycles: number;
}

export class PdfLink {
    downloadLink: string;
    next?: number;
    previous?: number;
}

@Injectable({
    providedIn: "root",
})
export class HtlService {
    public currentRegistrationMark: string;
    public helicopters: Helicopter[];
    public helicopterAttributes: any;
    public htlBlocks: HtlBlock[];

    public holdItemListArray: HoldItemList[] = [];

    public onHoldItemListInsert: EventEmitter<void> = new EventEmitter();

    constructor(private _http: HttpClient) {}

    public insertHtlBlock(htlBlock: HtlBlock): Observable<HtlBlock> {
        return this._http.put<HtlBlock>(environment.serverUrl + EndpointRoutes.INSERT_HTL_BLOCK, { htlBlock }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateHtlBlock(htlBlock: HtlBlock): Observable<HtlBlock> {
        return this._http.post<HtlBlock>(environment.serverUrl + EndpointRoutes.UPDATE_HTL_BLOCK, { htlBlock }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHelicopterHtlBlocks(helicopterId: string) {
        let params = new HttpParams();
        params = params.append("helicopterId", helicopterId);
        return this._http.get<HtlBlock[]>(environment.serverUrl + EndpointRoutes.GET_HTL_BLOCKS, { params }).pipe(
            map((responseData) => {
                this.htlBlocks = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHtlBlockById(htlBlockId: number): Observable<HtlBlock> {
        let params = new HttpParams();
        params = params.append("blockId", htlBlockId.toString());
        return this._http.get<HtlBlock>(environment.serverUrl + EndpointRoutes.GET_HTL_BLOCKS, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHtlDocumentsByBlockId(htlBlockId: number, desending: number): Observable<HTlDocument[]> {
        let params = new HttpParams();
        params = params.append("blockId", htlBlockId.toString());
        params = params.append("desending", desending.toString());
        return this._http.get<HTlDocument[]>(environment.serverUrl + EndpointRoutes.GET_HTL_DOCUMENTS, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHtlBlockNumbersByHelicopterId(helicopterId: number): Observable<HtlBlockNumber[]> {
        let params = new HttpParams();
        params = params.append("helicopterId", helicopterId.toString());
        return this._http.get<HtlBlockNumber[]>(environment.serverUrl + EndpointRoutes.GET_HTL_BLOCKS_NUMBERS, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    private _updateHoldItemList(object: HoldItemList): void {
        const filter = this.holdItemListArray.filter((h) => h.id === object.id)[0];
        const index = this.holdItemListArray.indexOf(filter);
        this.holdItemListArray[index] = object;
    }

    public getHoldItemListByBlockId(blockId: number): Observable<HoldItemList[]> {
        let params = new HttpParams();
        params = params.append("blockId", blockId.toString());
        return this._http.get<HoldItemList[]>(environment.serverUrl + EndpointRoutes.GET_HOLD_ITEM_LIST, { params }).pipe(
            map((responseData) => {
                this.holdItemListArray = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public insertNewHoldItemList(holdItem: HoldItemList, username: string): Observable<HoldItemList> {
        return this._http.put<HoldItemList>(environment.serverUrl + EndpointRoutes.INSERT_HOLD_ITEM_LIST, { holdItem, username }).pipe(
            map((responseData) => {
                this.holdItemListArray.push(responseData);
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateExpiringOfHoldItemList(expiring: Expiring, username: string): Observable<any> {
        return this._http.post<HoldItemList>(environment.serverUrl + EndpointRoutes.UPDATE_EXPIRING_HOLD_ITEM_LIST, { holdItem: expiring, username: username }).pipe(
            map((responseData) => {
                this._updateHoldItemList(responseData);
                this.onHoldItemListInsert.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateNextScheduledInspections(nextSchedule: NextScheduledInpsection): Observable<NextScheduledInpsection> {
        return this._http.post<NextScheduledInpsection>(environment.serverUrl + EndpointRoutes.UPDATE_NEXT_SCHEDULED_INSPECTION, { nextSchedule }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public generateHtlDocument(helicopterId: number, blockId: number, documentId: number): Observable<any> {
        const url = environment.serverUrl + EndpointRoutes.GENERATE_HTL_DOC;
        const body = { helicopterId, blockId, documentId };
        return this._http.post<any>(url, body).pipe(
            map((responseData) => responseData),
            catchError((errorRes) => throwError(errorRes)),
        );
    }

    public saveEditedColumns(editedHTLDocument: any): Observable<any> {
        editedHTLDocument.requestCode = uuid().replaceAll("-", "");
        editedHTLDocument.spreadsheets.forEach((spreadsheet) => {
            spreadsheet.requestCode = uuid().replaceAll("-", "");
            spreadsheet.columns.forEach((col) => {
                col.requestCode = uuid().replaceAll("-", "");
            });
        });

        return this._http.put<any>(environment.serverUrl + EndpointRoutes.SAVE_EDITED_COLUMNS, { editedHTLDocument }).pipe(
            map((responseData) => {
                return responseData;
            }),
        );
    }

    public getEnabledCells(): Observable<any> {
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.GET_ENABLED_CELLS).pipe(
            map((responseData) => {
                return responseData;
            }),
        );
    }

    public getEditedCells(documentId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append("htlDocumentId", documentId.toString());
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.GET_EDITED_CELLS, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
        );
    }

    public generateHtlBlockPdf(helicopterId: number, blockId: number): Observable<{ downloadLink: string }> {
        return this._http.post<{ downloadLink: string }>(environment.serverUrl + EndpointRoutes.GENERATE_HTL_DOC, { helicopterId, blockId }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getCurrentHelicopterHtlDocument(helicopterId: number, documentId?: number): Observable<PdfLink> {
        return this._http.post<PdfLink>(environment.serverUrl + EndpointRoutes.GENERATE_HTL_PUBLIC_PDF, { helicopterId, documentId }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public generateHoltItemListDocumentPdf(helicopterId: number, blockId: number): Observable<{ downloadLink: string }> {
        return this._http.post<{ downloadLink: string }>(environment.serverUrl + EndpointRoutes.GENERATE_HOLD_ITEM_PDF, { helicopterId, blockId }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public generateShortTermsDocumentPdf(helicopterId: number, blockId: number): Observable<{ downloadLink: string }> {
        return this._http.post<{ downloadLink: string }>(environment.serverUrl + EndpointRoutes.GENERATE_SHORT_TERMS_PDF, { helicopterId, blockId }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getDailyChecks(helicopterId: number, startDate?: Date, endDate?: Date): Observable<any> {
        const params: any = {
            helicopterId: helicopterId,
        };
        if (startDate && endDate) {
            params.startDate = moment(startDate).format("YYYY-MM-DD");
            params.endDate = moment(endDate).format("YYYY-MM-DD");
        }
        return this._http.post<any>(environment.serverUrl + EndpointRoutes.GET_DAILY_CHECKS, params).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public checkAllowedToEdit(helicotperId: number, htlDocumentId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append("helicopterId", helicotperId.toString());
        params = params.append("htlDocumentId", htlDocumentId.toString());
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.IS_ALLOWED, { params }).pipe(
            map((responseData) => {
                return responseData;
            }),
        );
    }

    public approveDocument(htlDocumentId: number): Observable<any> {
        return this._http.post<any>(environment.serverUrl + EndpointRoutes.APPROVE_DOCUMENT, { htlDocumentId }).pipe(
            map((responseData) => {
                return responseData;
            }),
        );
    }
}
